import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { FieldCategory, TextFieldDefinition } from '@fsx/fsx-shared';
import { FormControlWithModel } from '../../models';
import { FSXFormControlService } from '../../services';
import { ReferenceResolver } from '../../types';
import { Subject, debounceTime, takeUntil, tap } from 'rxjs';
import { FsxBaseComponent } from '../base/base.component';

@Component({
  selector: 'fsx-text-component',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
})
export class FsxTextComponent
  extends FsxBaseComponent
  implements OnInit, OnDestroy
{
  @Input() caption!: string;
  @Input() helpText!: string;
  @Input() hint: string = '';
  @Input() width!: string;
  @Input() height: string = '63px';
  @Input() fieldDefinition!: TextFieldDefinition | undefined;
  @Input() initialValue!: string;
  @Input() resolver!: ReferenceResolver;
  @Input() hidden = false;
  @Input() overflowWrap = false;

  public fieldType = FieldCategory.Text;

  @Output() formControlEmitter = new EventEmitter<
    FormControlWithModel<TextFieldDefinition>
  >();
  @Output() textChanged = new EventEmitter<string>();

  public formControl!: FormControlWithModel<TextFieldDefinition>;
  public inputFocused = false;

  private textChanged$$: Subject<string> = new Subject();
  private destroy$: Subject<unknown> = new Subject();

  constructor(private readonly fsxFormControlService: FSXFormControlService) {
    super();
  }

  ngOnInit(): void {
    if (this.fieldDefinition) {
      this.formControl = this.fsxFormControlService.createFormControl(
        this.fieldDefinition,
        this.fieldType,
        this.initialValue,
        this.resolver
      );
      this.formControlEmitter.emit(this.formControl);

      this.textChanged$$
        .pipe(
          debounceTime(700),
          tap((value: string) => {
            this.textChanged.emit(value);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public clearText(): void {
    if (this.formControl?.disabled) {
      return;
    }

    this.formControl?.setValue(null);
    this.formControl.markAsPristine();
  }

  public focusInput(): void {
    this.inputFocused = true;
  }

  public removeFocus(): void {
    this.inputFocused = false;
    this.formControl.updateValueAndValidity();
  }

  public getErrorMessage(): string {
    return Object.values({ ...this.formControl.errors })[0];
  }

  public formatValue(): void {
    const value: string = this.formControl.value;
    if (value && typeof value === 'string') {
      this.formControl.setValue(value.trim());
    }
  }

  public onFocus() {
    this.formatValue();
    this.focusInput();
  }

  public onTextChanged(event: KeyboardEvent) {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const value: string = target.value;
    this.textChanged$$.next(value);
  }

  public validate(): void {
    // ToDo: Add conditional validate fix here.
    let validate = true;
    if (validate) {
      const obj = Object(this.formControl.value);
      validate = !Object.keys(obj)
        .map(function (k) {
          return obj[k];
        })
        .reduce((a, b) => a && b === '', true);
    }
    if (this.shouldValidateFormControl(this.formControl)) {
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
      this.formControl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
